import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapList } from "services/apiTypes";

export type InstructorNotificationTargetData = {
  course_id?: number | string | null;
  announcement_id?: number | string | null;
  bundle_id?: number | string | null;
  url?: string | null;
  join_request_id?: number | string | null;
};

export type InstructorNotification = {
  id: number;
  notification_id: number;
  title: string | null;
  body: string | null;
  type: string;
  target_type: string;
  target_data: InstructorNotificationTargetData;
  is_read: boolean;
  read_at: string | null;
  created_at: string | null;
};

/** Resolve where an instructor inbox / push item should navigate. */
export function instructorNotificationPath(item: {
  type?: string | null;
  target_type?: string | null;
  target_data?: InstructorNotificationTargetData | null;
  course_id?: string | number | null;
  announcement_id?: string | number | null;
  bundle_id?: string | number | null;
  url?: string | null;
  notification_id?: string | number | null;
  click_action?: string | null;
  chat_path?: string | null;
  message_id?: string | null;
}): string {
  const type = String(item.type ?? "");
  const targetType = String(item.target_type ?? "none");
  const data = item.target_data ?? {};
  const courseId = item.course_id ?? data.course_id;
  const announcementId = item.announcement_id ?? data.announcement_id;
  const bundleId = item.bundle_id ?? data.bundle_id;
  const url = item.url ?? data.url;
  const clickAction = String(item.click_action ?? "");
  const chatPath = item.chat_path;
  const messageId = item.message_id;

  if (type === "bundle_join_request" || targetType === "join_requests") {
    return "/instructor/bundles";
  }

  if (
    type === "course_chat_message" ||
    targetType === "course_chat" ||
    clickAction === "open_course_chat" ||
    Boolean(chatPath) ||
    Boolean(messageId)
  ) {
    return courseId ? `/instructor/courses/${courseId}/chat` : "/instructor/default";
  }

  if (type === "course_announcement" || targetType === "course_announcement") {
    if (!courseId) return "/instructor/default";
    return `/instructor/courses/${courseId}/announcements${
      announcementId ? `?announcementId=${announcementId}` : ""
    }`;
  }

  if (type === "course" || targetType === "course") {
    return courseId ? `/instructor/courses/${courseId}/overview` : "/instructor/default";
  }

  if (type === "course_bundle" || targetType === "course_bundle") {
    return bundleId ? `/instructor/bundles/${bundleId}/edit` : "/instructor/bundles";
  }

  if (targetType === "url" && url) {
    return String(url);
  }

  return "/instructor/default";
}

export const instructorNotificationsService = {
  async getNotifications(params?: { page?: number; limit?: number }) {
    const res = await api.get<ApiEnvelope<PaginatedResult<InstructorNotification>>>(
      "/instructors/notifications",
      { params: { page: params?.page ?? 1, limit: params?.limit ?? 10 } }
    );
    return unwrapList<InstructorNotification>(res.data);
  },

  async markRead(notificationId: number) {
    await api.post(`/instructors/notifications/${notificationId}/read`);
  },

  async markAllRead() {
    await api.post("/instructors/notifications/read-all");
  },
};
